import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMutation } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import { Grid } from '@mui/material'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import AddIcon from '@mui/icons-material/Add'
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded'
import { getInfobarList } from 'app/redux/store/features/infobars'
import InfoGridFilter from './components/InfoGridFilter'
import InfoGridData from './components/InfoGridData'
import { Capitalize } from 'app/utils/components/StringUtils/stringUtils'
import { useSnackbar } from 'notistack'

const InfoBar = () => {
    const { state } = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const [filter, setFilter] = useState({
        fileName: '',
        sort: 'Heading',
        isDescending: false,
    })
    const { infobars, loading, error } = useSelector((state) => state.infobars)
    const [apiCallInProgress, setApiCallInProgress] = useState(false)
    const [currentPage, setCurrentPage] = useState(0)
    const pageSize = 25

    useEffect(() => {
        if (!apiCallInProgress || loading) return
        setApiCallInProgress(false)

        if (error) handleError(error)
    }, [loading])

    useEffect(() => {
        const shouldFetch = !state?.navigateFrom || !infobars.data?.length
        if (shouldFetch) {
            setApiCallInProgress(true)
            dispatch(getInfobarList({ ...filter, skip: 0, take: pageSize }))
        }
    }, [state?.navigateFrom, dispatch])

    function handleError(error) {
        if (error.status === '401') {
            navigate('/profile/signout')
        } else if (error.detail) {
            enqueueSnackbar(error.detail, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        } else {
            enqueueSnackbar(error, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }

    function init(initialValue) {
        return {
            gridFilterVisible: initialValue.gridFilterVisible,
            filterIconColor: initialValue.filterIconColor,
        }
    }

    function infoReducer(state, action) {
        switch (action.type) {
            case 'set-grid-filter':
                return {
                    ...state,
                    gridFilterVisible: action.payload,
                }
            case 'set-filter-icon-color':
                return {
                    ...state,
                    filterIconColor: action.payload,
                }
            default:
                return state
        }
    }

    const [infoGrid, setInfoGrid] = React.useReducer(
        infoReducer,
        {
            gridFilterVisible: false,
            filterIconColor: false,
        },
        init
    )

    const applyFilter = useMutation({
        mutationFn: (currentfilter) => {
            dispatch(getInfobarList({ ...currentfilter, skip: 0, take: pageSize }, true))
            setFilter(currentfilter)
        },
    })

    const onPageChange = (pageNumber) => {
        const currentFilter = { ...filter, skip: pageSize * pageNumber, take: pageSize }
        dispatch(getInfobarList(currentFilter))
        setFilter(currentFilter)
    }

    const onColumnSort = (sortOrder) => {
        const columnName = Capitalize(sortOrder?.[0]?.columnName || '')
        const currentFilter = {
            ...filter,
            sort: columnName,
            isDescending: sortOrder[0].direction !== 'asc',
            take: pageSize,
        }
        dispatch(getInfobarList(currentFilter))
        setFilter(currentFilter)
    }

    const onRefresh = () => {
        setApiCallInProgress(true)
        dispatch(getInfobarList({ ...filter, skip: 0, take: pageSize }, true))
        setCurrentPage(0)
    }

    const setGridFilter = React.useCallback(
        (value) => {
            setInfoGrid({ type: 'set-grid-filter', payload: value })
        },
        [setInfoGrid]
    )

    const onAddTerm = () => {
        navigate('/settings/configuration/info-bar/add')
    }

    const setFilterIconColor = React.useCallback(
        (value) => {
            setInfoGrid({ type: 'set-filter-icon-color', payload: value })
        },
        [setInfoGrid]
    )

    return (
        <JumboCardQuick
            noWrapper
            title={
                <Typography color={'common.white'} variant={'h3'}>
                    <AutorenewRoundedIcon
                        fontSize={'large'}
                        onClick={onRefresh}
                        sx={{
                            mb: -1.5,
                            mr: 0.5,
                            ml: -2,
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                    />
                    Info List
                </Typography>
            }
            headerSx={{
                height: 40,
                bgcolor: 'success.main',
            }}
            action={
                <Stack direction={'row'} spacing={4} sx={{ mt: -2 }}>
                    <AddIcon
                        fontSize={'large'}
                        variant={'contained'}
                        sx={{
                            pt: 0,
                            pb: 0,
                            color: 'common.white',
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                        onClick={onAddTerm}
                    />
                    <FilterAltIcon
                        fontSize='large'
                        sx={{
                            mt: -1.8,
                            color:
                                infoGrid.filterIconColor && !infoGrid.gridFilterVisible
                                    ? 'success.main'
                                    : 'common.white',
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                        onClick={() => setGridFilter(!infoGrid.gridFilterVisible)}
                    />
                </Stack>
            }>
            <Grid>
                <InfoGridFilter
                    mutation={applyFilter}
                    display={infoGrid.gridFilterVisible ? '' : 'none'}
                    filterIconColor={(data) => setFilterIconColor(data)}
                />
            </Grid>
            <Grid>
                <InfoGridData
                    onRefresh={onRefresh}
                    onPageChange={onPageChange}
                    onColumnSort={onColumnSort}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    handleError={handleError}
                />
            </Grid>
        </JumboCardQuick>
    )
}

export default InfoBar
